import { FC, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';

type ConfirmationStatus = 'confirming' | 'success' | 'expired';

const ConfirmEmailPage: FC = () => {
    const { code } = useParams<{ code: string }>();
    const [status, setStatus] = useState<ConfirmationStatus>('confirming');
    const [isLoadingResend, setIsLoadingResend] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);

    useEffect(() => {
        const confirmEmail = async () => {
            if (!code) {
                setStatus('expired');
                setFeedbackMessage("Codice di conferma non trovato nell'URL.");
                return;
            }

            try {
                const response = await api.get(`/api/users/confirmEmail/${code}`);
                if (response.data.status && response.data.data === true) {
                    setStatus('success');
                } else {
                    throw new Error("Link di conferma scaduto o non valido.");
                }
            } catch (err) {
                setStatus('expired');
                setFeedbackMessage("Link di conferma scaduto o non valido.");
            }
        };

        confirmEmail();
    }, [code]);

    const handleResendEmail = async () => {
        if (!code) return;

        setIsLoadingResend(true);
        setFeedbackMessage(null);
        try {
            const response = await api.get(`/api/users/resendConfirmEmailByToken/${code}`);
            if (response.data.status) {
                setFeedbackMessage('Una nuova email di conferma è stata inviata con successo!');
            } else {
                throw new Error(response.data.message || "Impossibile inviare una nuova email.");
            }
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || err.message || "Si è verificato un errore.";
            setFeedbackMessage(errorMsg);
        } finally {
            setIsLoadingResend(false);
        }
    };

    const renderContent = () => {
        switch (status) {
            case 'confirming':
                return (
                    <div className="text-center">
                        <p className="text-slate-400 text-lg">Conferma dell'account in corso...</p>
                    </div>
                );
            case 'success':
                return (
                    <div className="text-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-400 mx-auto mb-6"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                        <h1 className="text-2xl font-bold text-white mb-4">Email Confermata!</h1>
                        <p className="text-slate-400 mb-6">
                            Il tuo account è stato attivato. Ora attendi l'approvazione di un amministratore per poter accedere.
                        </p>
                        <Link to="/login" className="px-6 py-2 rounded-lg text-white font-semibold bg-sky-600 hover:bg-sky-500 transition-colors">
                            Vai al Login
                        </Link>
                    </div>
                );
            case 'expired':
                return (
                    <div className="text-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-400 mx-auto mb-6"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                        <h1 className="text-2xl font-bold text-white mb-4">Link Scaduto</h1>
                        <p className="text-slate-400 mb-6">
                            {feedbackMessage || "Questo link di conferma non è più valido. Clicca qui sotto per ricevere una nuova email."}
                        </p>
                        <button
                            onClick={handleResendEmail}
                            disabled={isLoadingResend}
                            className="w-full px-5 py-3 rounded-lg text-white font-semibold bg-sky-600 hover:bg-sky-500 transition-colors disabled:bg-slate-600"
                        >
                            {isLoadingResend ? 'Invio in corso...' : 'Invia una nuova email di conferma'}
                        </button>
                        <Link to="/login" className="mt-6 inline-block text-sm text-sky-400 hover:text-sky-300">Torna al Login</Link>
                    </div>
                );
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 flex flex-col justify-center items-center p-4">
            <div className="w-full max-w-md">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default ConfirmEmailPage;
